import { Trans, useTranslation } from 'next-i18next';
import { DSButton, DSHeading, DSIcon, DSLinkButton, DSText } from '@stihl-design-system/components';

import { PhoenixImage } from '@phoenix/core/frontend/shared/ui/assets/phoenix-image';
import { useToolShedToast } from '@phoenix/team-m/my-tool-shed/frontend/ui/notifications/useToast';
import { Voucher as VoucherProps } from '@phoenix/team-m/my-tool-shed/frontend/domain/model/benefits.model';
import { LinkOut } from '@phoenix/core/frontend/shared/ui/link-out/link-out';
import { useUserLocale } from '@phoenix/core/frontend/shared/util/locales/use-user-locale';
import { getLocale } from '@phoenix/core/frontend/shared/util/locales/locales';

export const Voucher: React.FC<VoucherProps> = ({ promotionId, images, title, voucherCode, callToActionURL }) => {
  const { t } = useTranslation();
  const locale = useUserLocale() || getLocale();
  const { showSuccessToast } = useToolShedToast();

  const copyToClipboard = () => {
    navigator.clipboard.writeText(voucherCode).then(() => {
      showSuccessToast(t('myBenefits_toast_copyVoucherSuccess'));
    });
  };

  return (
    <div className="bg-secondary-light flex flex-col-reverse tablet:flex-row text-center tablet:text-start">
      <div className="flex-1 flex flex-col justify-between pt-9 tablet:pt-11 px-4 tablet:px-10 pb-4">
        <div className="max-w-sm m-auto tablet:m-0">
          <DSHeading tag="h2" size="large-uppercase" className="text-wrap">
            {title}
          </DSHeading>
          <DSText className="mt-2">{t('myBenefits_card_descriptionVoucher')}</DSText>
          <div className="flex flex-col gap-3">
            <DSButton
              className="w-full tablet:w-fit mt-5"
              iconName="two-sheets"
              iconPosition="right"
              variant="outline"
              onClick={copyToClipboard}
            >
              {voucherCode}
            </DSButton>
            {callToActionURL && (
              <DSLinkButton className="w-full tablet:w-fit" href={callToActionURL}>
                {t('myBenefits_card_buttonGoToShop')}
              </DSLinkButton>
            )}
          </div>
        </div>
        <DSText className="mt-4">
          <Trans
            i18nKey="toolShed_myEquipment_voucher_legalConditions"
            components={{
              termsLink: <LinkOut variant="highlight" href={`/${locale}/tool-shed/legal-conditions/voucher?id=${promotionId}`} />,
            }}
          />
        </DSText>
      </div>
      <div className="relative h-1 tablet:h-auto w-full tablet:w-1 z-50 bg-primary-orange">
        <div className="absolute inset-x-0 tablet:-inset-x-7 inset-y-1/2 tablet:inset-y-3/4 desktop:inset-y-0 m-auto w-14 h-14 desktop:w-16 desktop:h-16 flex items-center justify-center bg-primary-orange rounded-full">
          <DSIcon name="percent" size="x-large" />
        </div>
      </div>
      <div className="relative w-full tablet:w-1/3 desktop:w-1/2 tablet:min-h-[376px] min-h-[224px]">
        <PhoenixImage src={images.main?.url || ''} className="tabletOnly:scale-125" layout="fill" objectFit="cover" />
      </div>
    </div>
  );
};
